/**
 * "Back under the banner on a screen change" (mock `_kit/shell.js` screenTop, 7 Oct 2026).
 *
 * A route change inside the same page does not scroll: switching tabs or organizer views left the
 * visitor in the middle of the new screen. On a screen-key change we go back up — to 0 without an
 * anchor, or, with a sticky anchor (the hub's tab bar), just enough for it to reach its stuck
 * position (banner just scrolled out). We never scroll down: above the target nothing moves.
 *
 * `routeY` must be the position read BEFORE the new screen rendered: a shorter document has already
 * clamped `scrollY` by the time the effect runs (the mock's ROUTE_Y) — hence the passive listener.
 */
let lastScrollY = 0
let traversePending = false
let listening = false

/**
 * Starts the two listeners once: the scroll position (ROUTE_Y) and `popstate`, which fires on the
 * browser's back / forward before React renders the restored location. (React Router's
 * useNavigationType reports POP for everything inside a `<Routes location>` override, so it cannot
 * tell a Link from a Back.)
 */
export const trackScrollY = () => {
    if (listening || typeof window === 'undefined') return
    listening = true
    lastScrollY = window.scrollY
    window.addEventListener(
        'scroll',
        () => {
            lastScrollY = window.scrollY
        },
        { passive: true },
    )
    window.addEventListener('popstate', () => {
        traversePending = true
    })
}

export const getRouteY = () => lastScrollY

/** True once after a back / forward navigation; reading it clears it. */
export const consumeTraverse = () => {
    const traverse = traversePending
    traversePending = false
    return traverse
}

/**
 * Where to scroll on a screen change. `null` = leave the position alone.
 * - `changed` false (same screen, or the first screen seen): null
 * - `traverse` (browser back / forward): null, the browser restored its position
 * - no anchor: 0
 * - anchor: min(routeY, anchorTop − stickyTop) clamped at 0 — never further down than where we were
 */
export function resolveScreenTop({ changed, traverse, routeY, anchorTop, anchorStickyTop = 0 }) {
    if (!changed || traverse) return null
    if (anchorTop === undefined || anchorTop === null) return 0
    const target = anchorTop - (anchorStickyTop || 0)
    return Math.max(0, Math.min(routeY, target))
}
